import { useCallback, useMemo, useState } from 'react';

import { LockOutlined, MoreOutlined } from '@ant-design/icons';
import { Dropdown, Tag } from 'antd';
import type { MenuProps } from 'antd';

import { getAssetTypeName } from '@/shared/api/openmetadata';
import { SEMANTIC_COLOR, SEMANTIC_DIMENSION } from '@/shared/design-tokens/design-tokens';
import { useAuthorization } from '@/shared/hooks';
import { getAssetIconStyle, getAssetIconWrapperStyle } from '@/shared/lib/asset';
import { Button } from '@/shared/ui/Button';
import { ConfirmModal } from '@/shared/ui/ConfirmModal';
import { ContentHeader } from '@/shared/ui/ContentHeader';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';

import { useDeleteClassification, useTagCategoryDetail } from '@/features/tags/model';
import { ClassificationFormModal } from '@/features/tags/ui/ClassificationFormModal';
import { TagFormModal } from '@/features/tags/ui/TagFormModal';

import styles from './TagCategoryHeader.module.css';

type TagCategoryHeaderProps = {
  selectedTagCategory: string;
  onTagCreated?: (parentFqn: string) => void;
  onClassificationUpdated?: (parentFqn: string) => void;
};

export const TagCategoryHeader = ({
  selectedTagCategory,
  onTagCreated,
  onClassificationUpdated,
}: TagCategoryHeaderProps) => {
  const [isTagFormModalOpen, setIsTagFormModalOpen] = useState(false);
  const [isClassificationEditModalOpen, setIsClassificationEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { data } = useTagCategoryDetail({
    categoryName: selectedTagCategory ?? '',
  });

  const { handleDelete } = useDeleteClassification(selectedTagCategory, {
    onTreeRefresh: onClassificationUpdated,
  });

  const displayName = data?.displayName || data?.name;
  const description = data?.description ?? '-';
  const isMutuallyExclusive = data?.mutuallyExclusive;
  const isSystemTag = data?.provider === 'system';

  const { hasAnyRole } = useAuthorization();
  const canManageCategories = hasAnyRole(['admin', 'manager']);

  const handleAddTagClick = useCallback(() => {
    setIsTagFormModalOpen(true);
  }, []);

  const handleTagFormModalClose = useCallback(() => {
    setIsTagFormModalOpen(false);
  }, []);

  const handleTagFormSuccess = useCallback(
    (parentFqn: string) => {
      onTagCreated?.(parentFqn);
    },
    [onTagCreated]
  );

  const handleEditClick = useCallback(() => {
    setIsClassificationEditModalOpen(true);
  }, []);

  const handleClassificationEditModalClose = useCallback(() => {
    setIsClassificationEditModalOpen(false);
  }, []);

  const handleClassificationEditSuccess = useCallback(
    (parentFqn: string) => {
      onClassificationUpdated?.(parentFqn);
    },
    [onClassificationUpdated]
  );

  const handleDeleteClick = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteExecute = useCallback(async () => {
    await handleDelete(data?.id ?? '');
  }, [handleDelete, data?.id]);

  const deleteMessage = useMemo(() => {
    return `"${displayName}" 분류를 삭제하시겠습니까?\n하위 태그도 모두 삭제됩니다.`;
  }, [displayName]);

  const iconConfig = useMemo(() => {
    const { iconColor } = getAssetIconStyle('tag');
    return {
      component: <Icon name="tag" className="icon" color={iconColor} />,
      wrapperStyle: getAssetIconWrapperStyle('tag'),
      tooltip: getAssetTypeName('tag'),
    };
  }, []);

  const breadcrumbItems = useMemo(() => {
    return [
      {
        title: (
          <span className={styles.breadcrumbItem}>
            <EllipsisText text={displayName ?? ''} className={styles.breadcrumbText} />
          </span>
        ),
      },
    ];
  }, [displayName]);

  const parentInfo = useMemo(
    () => ({
      classification: selectedTagCategory ?? '',
      classificationName: displayName ?? '',
    }),
    [selectedTagCategory, displayName]
  );

  const dropdownItems: MenuProps['items'] = useMemo(() => {
    if (isSystemTag) return [];
    return [
      {
        key: 'edit',
        icon: <Icon name="edit" size={16} />,
        label: '수정',
        onClick: handleEditClick,
      },
      {
        key: 'delete',
        icon: <Icon name="trash" width={13.6} />,
        label: '삭제',
        onClick: handleDeleteClick,
      },
    ];
  }, [isSystemTag, handleEditClick, handleDeleteClick]);

  return (
    <>
      <ContentHeader
        breadcrumbItems={breadcrumbItems}
        icon={iconConfig}
        title={displayName}
        description={description}
        titleRowExtra={
          <>
            {isSystemTag && (
              <Tag
                style={{
                  color: SEMANTIC_COLOR.ON_CONTAINER.PRIMARY.LEVEL2,
                  backgroundColor: SEMANTIC_COLOR.CONTAINER.PRIMARY.ALT_LEVEL2,
                  height: SEMANTIC_DIMENSION.REGULAR,
                }}
                icon={<LockOutlined />}>
                시스템 태그
              </Tag>
            )}
            {isMutuallyExclusive && <Tag style={{ height: SEMANTIC_DIMENSION.REGULAR }}>상호 배타적</Tag>}
          </>
        }
        actions={
          !isSystemTag &&
          canManageCategories && (
            <>
              <Button prefix={<Icon name="add" size={16} />} onClick={handleAddTagClick}>
                태그 추가
              </Button>
              <Dropdown
                menu={{ items: dropdownItems }}
                trigger={['click']}
                placement="bottomRight"
                rootClassName={styles.dropdown}>
                <button type="button" className={styles.moreButton}>
                  <MoreOutlined className={styles.icon} />
                </button>
              </Dropdown>
            </>
          )
        }
      />

      <TagFormModal
        open={isTagFormModalOpen}
        mode="create"
        parentInfo={parentInfo}
        onClose={handleTagFormModalClose}
        onSuccess={handleTagFormSuccess}
      />

      <ClassificationFormModal
        open={isClassificationEditModalOpen}
        mode="edit"
        classificationData={data}
        onClose={handleClassificationEditModalClose}
        onSuccess={handleClassificationEditSuccess}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        message={deleteMessage}
        confirmText="삭제"
        cancelText="취소"
        confirmButtonProps={{ danger: true }}
        onConfirm={handleDeleteExecute}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </>
  );
};
